import { Link } from 'react-router-dom';

interface ArticleCardProps {
  title: string;
  slug: string;
  excerpt: string;
  imageUrl: string;
  author: string;
  publishDate: string;
  readTime: string;
  categoryName?: string;
  categorySlug?: string;
  tags?: string[];
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({
  title,
  slug,
  excerpt,
  imageUrl,
  author,
  publishDate,
  readTime,
  categoryName,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200/60 transition-all duration-300"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative h-56 md:h-full overflow-hidden">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background-50/20"></div>
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            {categoryName && (
              <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-3 w-fit">
                {categoryName}
              </span>
            )}
            <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 group-hover:text-primary-700 transition-colors mb-3 leading-snug">
              {title}
            </h3>
            <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-3">
              {excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {readTime}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug">
            {title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
            <span>{publishDate}</span>
            <span>{readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200/60 transition-all duration-300"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {categoryName && (
          <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-background-50/90 text-foreground-700 text-xs font-medium">
            {categoryName}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-700 transition-colors mb-2 leading-snug line-clamp-2">
          {title}
        </h3>
        <p className="text-foreground-500 text-sm leading-relaxed mb-4 line-clamp-2">
          {excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {readTime}
            </span>
          </div>
          <span className="text-primary-600 text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            阅读
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}